import { DataTableCellV2, DataTableRowV2 } from '@americanexpress/dls-react';
import Selectors from 'src/utils/selectors';
import { Align } from 'src/types/align.enum';
import React from 'react';
import { TransactionModel } from 'src/types/transaction.model';
import TransactionCategoryCell from 'src/components/atomic/molecules/transactionCategoryCell/TransactionCategoryCell';
import TransactionFrequencyCell from 'src/components/atomic/molecules/transactionFrequencyCell/TransactionFrequencyCell';
import { DlsTypographyVariants } from 'src/types/dls-typography-variants.enum';
import TransactionNameAccount from 'src/components/atomic/atoms/transactionNameAccount/TransactionNameAccount';
import TransactionAmount from 'src/components/atomic/atoms/transactionAmount/TransactionAmount';
import EditTransactionButton from 'src/components/atomic/atoms/editTransactionButton/EditTransactionButton';
import { useBreakpoints } from 'src/hooks/useBreakpoints';
import ApproveTransactionButton from 'src/components/atomic/atoms/approveTransactionButton/ApproveTransactionButton';
import TransactionDate from 'src/components/atomic/atoms/transactionDate/TransactionDate';
import AutoSaveTransaction from 'src/components/auto-save/AutoSaveTransaction';
import styles from './transactionRow.module.scss';
import TransactionTypeText from 'src/components/atomic/atoms/transactionTypeText/TransactionTypeText';
import { useUpdatingTransaction } from 'src/hooks/useUpdatingTransaction';
import TransactionTypeSkeletonLoader from 'src/components/atomic/atoms/transactionTypeSkeletonLoader/TransactionTypeSkeletonLoader';
import { Formik, useFormikContext } from 'formik';
import * as Yup from 'yup';
import { trackClick } from 'src/hooks/useAdobeTags';
import adobeTags from 'src/types/adobe-tags';
import { useReviewTransaction } from 'src/hooks/queries/transactions';
import { useLocation } from 'react-router-dom';
import { TransactionReviewForm } from 'src/components/atomic/organisms/unified-transactions/TransactionsReviewView';
import { size } from 'lodash';
import { Categories } from 'src/services/category.service';
import { WalkmeName, WalkmePopupWrapper } from '../walkme';
import { CategoryModel } from 'src/types/category.model';

interface Props {
    transaction: TransactionModel;
    categories: CategoryModel[];
    isReview?: boolean;
    rowIndex: number;
    checked: boolean; // This needs to be named checked for DLS "Select All" checkbox to function correctly
    setDisplayingTransactionRulePrompt?: (transaction: TransactionModel) => void;
}

export default function TransactionRow({
    transaction,
    categories,
    isReview,
    rowIndex,
    checked,
    setDisplayingTransactionRulePrompt
}: Props): JSX.Element {
    const { isMinWidthMedium, isMinWidthLarge, isMinWidthExtraLarge } = useBreakpoints();
    const { pathname } = useLocation();
    const { mutateAsync: reviewTransaction } = useReviewTransaction();
    const formikContext = useFormikContext();
    const errors = formikContext.errors as TransactionReviewForm;
    const hasBulkError = errors?.selectedTransactions?.some((t) => {
        if (t) {
            const transactionId = Number(t.groupUid.split(' ').pop());
            return transaction.id === transactionId;
        } else return false;
    });
    const { isUpdatingTransaction } = TransactionRow.useUpdatingTransaction(transaction.id, true, checked);

    const handleSubmitReviewTransaction = async (): Promise<void> => {
        trackClick(pathname, adobeTags.Transactions.Row.Approve);
        await reviewTransaction(transaction.id);
    };

    const handleCategoryChange = (groupUid: string): void => {
        if (setDisplayingTransactionRulePrompt) {
            setDisplayingTransactionRulePrompt({ ...transaction, groupUid });
        }
    };

    const getWalkmeWrappedCategoryCell = (
        transactionArg: TransactionModel,
        hasErrorArg: boolean,
        baseFieldIdArg: string
    ): JSX.Element => (
        <WalkmePopupWrapper walkmeName={WalkmeName.TRANSACTION_CLASSIFICATION} stepIndex={4}>
            <TransactionCategoryCell
                transaction={transactionArg}
                categories={categories}
                hideLabel
                className={hasErrorArg ? styles.errorCategoryCell : ''}
                hasError={hasErrorArg}
                baseFieldId={baseFieldIdArg}
                containerClassName={styles.minWidth100Pct}
                isReview={isReview}
                handleCategoryChange={handleCategoryChange}
            />
        </WalkmePopupWrapper>
    );

    const getWalkmeWrappedTransactionNameAccount = (transactionArg: TransactionModel): JSX.Element => (
        <WalkmePopupWrapper
            walkmeName={WalkmeName.TRANSACTION_CLASSIFICATION}
            stepIndex={3}
            overrides={{ left: '-44%' }}
        >
            <TransactionNameAccount
                transactionId={transactionArg.id}
                accountId={transactionArg.accountId}
                transactionDisplayName={transactionArg.displayName}
                transactionDate={transactionArg.date}
                merchantName={transactionArg.merchantName}
            />
        </WalkmePopupWrapper>
    );

    return (
        <Formik
            initialValues={{ transaction }}
            validationSchema={Yup.object({
                transaction: Yup.object().shape({
                    groupUid: Yup.string().test('needsCategory', (value) => {
                        return isReview ? value !== Categories.UncategorizedGroupId : true;
                    })
                })
            })}
            enableReinitialize
            validateOnBlur={false}
            onSubmit={isReview ? handleSubmitReviewTransaction : () => {}}
        >
            {({ handleSubmit, errors }) => {
                const hasError = size(errors) > 0 || hasBulkError;
                const baseFieldId: string = 'transaction';
                return (
                    <DataTableRowV2
                        id={`${isReview ? 'transactionReviewRow' : 'transactionRow'}-${rowIndex}-${transaction.id}`}
                        checkboxLabel={transaction.displayName}
                        checked={checked}
                        data-cy={isReview ? Selectors.Transactions.REVIEW_ROW : Selectors.Transactions.TABLE_ROW}
                        className={`${styles.transactionRow} ${hasError ? styles.errorRow : ''}`}
                    >
                        <DataTableCellV2 className={styles.nameCell}>
                            {rowIndex === 0 && isMinWidthLarge && !isMinWidthExtraLarge && !isReview ? (
                                getWalkmeWrappedTransactionNameAccount(transaction)
                            ) : (
                                <TransactionNameAccount
                                    transactionId={transaction.id}
                                    accountId={transaction.accountId}
                                    transactionDisplayName={transaction.displayName}
                                    transactionDate={transaction.date}
                                    merchantName={transaction.merchantName}
                                />
                            )}
                        </DataTableCellV2>
                        <DataTableCellV2 align={Align.RIGHT}>
                            <TransactionAmount amount={transaction.amount} />
                        </DataTableCellV2>
                        <DataTableCellV2 align={Align.CENTER}>
                            <TransactionDate date={transaction.date} className={DlsTypographyVariants.BODY_1} />
                        </DataTableCellV2>
                        {isMinWidthExtraLarge && (
                            <DataTableCellV2
                                data-cy={Selectors.Transactions.TYPE_COLUMN}
                                align={isUpdatingTransaction ? Align.CENTER : Align.LEFT}
                            >
                                {isUpdatingTransaction ? (
                                    <TransactionTypeSkeletonLoader />
                                ) : (
                                    <TransactionTypeText transactionType={transaction.type} />
                                )}
                            </DataTableCellV2>
                        )}
                        <DataTableCellV2 className={hasError ? styles.categoryCellError : styles.categoryCell}>
                            {rowIndex === 0 && isMinWidthMedium && !isReview ? (
                                getWalkmeWrappedCategoryCell(transaction, hasError, baseFieldId)
                            ) : (
                                <TransactionCategoryCell
                                    transaction={transaction}
                                    categories={categories}
                                    hideLabel
                                    className={hasError ? styles.errorCategoryCell : ''}
                                    hasError={hasError}
                                    baseFieldId={baseFieldId}
                                    isReview={isReview}
                                    handleCategoryChange={handleCategoryChange}
                                />
                            )}
                        </DataTableCellV2>
                        <DataTableCellV2 className={styles.frequencyCell}>
                            <TransactionFrequencyCell hideLabel baseFieldId={baseFieldId} isReview={isReview} />
                        </DataTableCellV2>
                        <DataTableCellV2 align={isReview ? Align.CENTER : undefined}>
                            <EditTransactionButton
                                transactionId={transaction.id}
                                transactionName={transaction.displayName}
                            />
                        </DataTableCellV2>
                        {isReview && (
                            <DataTableCellV2 align={Align.LEFT} className={styles.approveCell}>
                                <ApproveTransactionButton
                                    transactionName={transaction.displayName}
                                    onClick={handleSubmit}
                                />
                            </DataTableCellV2>
                        )}
                        <AutoSaveTransaction
                            transaction={transaction}
                            baseFieldId={`${baseFieldId}.`}
                            blockAutoSubmit
                            handleCategoryChange={handleCategoryChange}
                        />
                    </DataTableRowV2>
                );
            }}
        </Formik>
    );
}

TransactionRow.useUpdatingTransaction = useUpdatingTransaction;
